import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { getMixedPalette } from "design-system/lib/chart-utils"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "design-system/molecules/Card"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "design-system/molecules/Chart"
import { cn } from "design-system/utils"
import { formatMoney, isNil } from "lopermut"
import { RadarIcon } from "lucide-react"
import { LAST_VALUE_FIELDS } from "modules/backoffice/lib/bi/biBuckets"
import { Loading } from "modules/shared/components/Loading"
import { ACTIVITY_BAR_SIZE } from "modules/shared/lib/charts"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { BiCategoryField } from "schema"

const formatRateValue = (value: number, field: string): string => {
  if (
    field === BiCategoryField.Conversation.EngagementRate ||
    field === BiCategoryField.Conversation.AchievementRate
  ) {
    return `${(value * 100).toFixed(2)}%`
  }
  if (
    field === BiCategoryField.Conversation.AvgTimeToEngage ||
    field === BiCategoryField.Conversation.AvgTimeToAchieve
  ) {
    // Value is already in minutes
    const totalMinutes = Math.floor(value)
    const hours = Math.floor(totalMinutes / 60)
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    const remainingMinutes = totalMinutes % 60
    if (days > 0) return `${days}d ${remainingHours}h`
    if (hours > 0) return `${hours}h ${remainingMinutes}m`
    return `${totalMinutes}m`
  }
  if (
    field === BiCategoryField.Message.AvgResponseTime ||
    field === BiCategoryField.Message.P95ResponseTime
  ) {
    // Value is in seconds
    if (value < 60) return `${value.toFixed(1)}s`
    const minutes = Math.floor(value / 60)
    const seconds = Math.round(value % 60)
    return `${minutes}m ${seconds}s`
  }
  return value.toFixed(2)
}

type ChartDataPoint = {
  date: string
  [key: string]: string | number
}

type MetricChartCardProps = {
  category: string
  field: string
  title: string
  description: string
  chartData: ChartDataPoint[]
  chartConfig: ChartConfig
  hasData: boolean
  isPending: boolean
  type: "Money" | "Count" | "Rate"
}

const chartColors = getMixedPalette()

export const MetricChartCard = ({
  category,
  field,
  title,
  description,
  chartData,
  chartConfig,
  hasData,
  isPending,
  type,
}: MetricChartCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const chartKeys = Object.keys(chartConfig)
  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set(chartKeys))

  const totalCost = useMemo(() => {
    if (type !== "Money") return null

    if (LAST_VALUE_FIELDS.some((chart) => chart.category === category && chart.field === field)) {
      const lastDayData = chartData[chartData.length - 1]
      if (!lastDayData) return 0
      return Object.entries(lastDayData)
        .filter(([key]) => key !== "date")
        .reduce((sum, [, value]) => sum + Number(value), 0)
    }

    return chartData.reduce((acc, data) => {
      return (
        acc +
        Object.entries(data)
          .filter(([key]) => key !== "date")
          .reduce((sum, [, value]) => sum + Number(value), 0)
      )
    }, 0)
  }, [category, chartData, field, type])

  const averageRate = useMemo(() => {
    if (type !== "Rate") return null

    // Don't show average for percentage metrics (EngagementRate, AchievementRate)
    // Averaging percentages across different denominators is statistically incorrect
    if (
      field === BiCategoryField.Conversation.EngagementRate ||
      field === BiCategoryField.Conversation.AchievementRate
    ) {
      return null
    }

    const allValues = chartData.flatMap((data) =>
      Object.entries(data)
        .filter(([key]) => key !== "date")
        .map(([, value]) => Number(value)),
    )

    if (allValues.length === 0) return null
    const avg = allValues.reduce((sum, val) => sum + val, 0) / allValues.length
    return formatRateValue(avg, field)
  }, [chartData, field, type])

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData])

  if (isPending) {
    return (
      <Card className="flex flex-col h-full w-full">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-80">
          <Loading />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="flex flex-col h-full w-full">
      <CardHeader className={cn("pb-2", (!isNil(totalCost) || !isNil(averageRate)) && "pr-10")}>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        {!isNil(totalCost) && (
          <CardAction className="p-3 text-lg font-medium text-foreground font-mono">
            <Tooltip>
              <TooltipTrigger slick>
                {formatMoney({ amount: totalCost, fix: 2, currency: "USD" })}
              </TooltipTrigger>
              <TooltipContent>{t("backoffice.bi.totalCostTooltip")}</TooltipContent>
            </Tooltip>
          </CardAction>
        )}
        {!isNil(averageRate) && (
          <CardAction className="p-3 text-lg font-medium text-foreground font-mono">
            {averageRate}
          </CardAction>
        )}
      </CardHeader>
      {!hasData && (
        <CardContent className="flex items-center justify-center h-80">
          <div className="flex flex-col gap-2 items-center text-center">
            <RadarIcon className="size-8 text-muted-foreground" strokeWidth={1.5} />
            <div className="flex flex-col items-center text-center">
              <h2 className="text-base text-foreground">{t("shared.emptyState.title")}</h2>
              <p className="text-sm text-muted-foreground">{t("shared.emptyState.description")}</p>
            </div>
          </div>
        </CardContent>
      )}
      {hasData && (
        <CardContent
          ref={containerRef}
          className={cn(
            "flex flex-col py-2 overflow-x-auto",
            !hasData && "pointer-events-none opacity-50",
          )}
        >
          <ChartContainer
            config={chartConfig}
            className="aspect-auto min-h-80 h-full"
            style={{
              width: `${(chartData?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
              minWidth: "100%",
            }}
          >
            <AreaChart accessibilityLayer data={chartData} margin={{ bottom: 10 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
              />
              <YAxis tickLine={false} axisLine={false} tickMargin={10} width={30} hide={true} />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    formatter={(value, name, item) => {
                      const dataKey = (item.dataKey || name || "").toString()
                      const formattedValue = formatRateValue(Number(value), field)
                      const Icon = chartConfig[dataKey]?.icon

                      return (
                        <>
                          {Icon ? (
                            <Icon />
                          ) : (
                            <div
                              className="shrink-0 h-2.5 w-2.5 rounded-full border"
                              style={{
                                backgroundColor: item.color as string,
                                borderColor: item.color as string,
                              }}
                            />
                          )}
                          <div className="grid flex-1 grid-cols-[1fr_auto] gap-x-3 leading-none items-center">
                            <div className="grid gap-1.5">
                              <span className="text-muted-foreground">
                                {chartConfig[dataKey]?.label || name}
                              </span>
                            </div>
                            <span className="text-foreground font-mono font-medium tabular-nums">
                              {formattedValue}
                            </span>
                          </div>
                        </>
                      )
                    }}
                  />
                }
              />
              {Object.keys(chartConfig).map((key, index) => {
                const config = chartConfig[key]
                const color = config?.color || `var(--${chartColors[index % chartColors.length]})`
                const isActive = activeKeys.has(key)

                return (
                  <Area
                    key={key}
                    name={typeof config?.label === "string" ? config.label : key}
                    dataKey={key}
                    stackId="a"
                    stroke={color}
                    fill={color}
                    fillOpacity={isActive ? 0.6 : 0}
                    strokeWidth={isActive ? 2 : 0}
                    strokeOpacity={isActive ? 1 : 0}
                    type="monotone"
                    hide={!isActive}
                  />
                )
              })}
              <ChartLegend
                content={
                  <ChartLegendContent
                    maxItems={6}
                    viewMoreLabel={t("shared.viewMore")}
                    viewLessLabel={t("shared.viewLess")}
                    onLegendItemClick={(dataKey) => {
                      setActiveKeys((prev) => {
                        const isOnlyActive = prev.size === 1 && prev.has(dataKey)
                        if (isOnlyActive) {
                          return new Set(chartKeys)
                        }
                        return new Set([dataKey])
                      })
                    }}
                    activeKeys={activeKeys}
                  />
                }
              />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      )}
    </Card>
  )
}
